import type { CSSProperties, ReactNode } from 'react';
import {
  Select as AriaSelect,
  SelectValue,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Label,
  Text,
  type Key,
  type SelectProps as AriaSelectProps,
} from 'react-aria-components';
import { ChevronDown } from 'lucide-react';
import { useInterfacePortalContainer } from '@/foundation/ui/InterfacePortalContext';
import { cn } from '@/foundation/utils/cn';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  AriaSelectProps<SelectOption>,
  'children' | 'items' | 'className' | 'style' | 'label' | 'description' | 'errorMessage'
> {
  label?: ReactNode;
  description?: ReactNode;
  errorMessage?: ReactNode;
  size?: SelectSize;
  options: SelectOption[];
  className?: string;
  style?: CSSProperties;
  triggerStyle?: CSSProperties;
  children?: (option: SelectOption) => ReactNode;
}

const SIZE_CONFIG: Record<
  SelectSize,
  {
    height: number | string;
    fontSize: number | string;
    radius: number | string;
    paddingInline: number;
    labelSize: number | string;
  }
> = {
  sm: {
    height: 'var(--control-height-sm)',
    fontSize: 'var(--text-xs)',
    radius: 'var(--control-radius-sm)',
    paddingInline: 14,
    labelSize: 'var(--text-xs)',
  },
  md: {
    height: 'var(--control-height-md)',
    fontSize: 'var(--text-sm)',
    radius: 'var(--control-radius-md)',
    paddingInline: 16,
    labelSize: 'var(--text-xs)',
  },
  lg: {
    height: 'var(--control-height-lg)',
    fontSize: 'var(--text-base)',
    radius: 'var(--control-radius-lg)',
    paddingInline: 18,
    labelSize: 'var(--text-sm)',
  },
};

function getTriggerStyle(
  config: (typeof SIZE_CONFIG)[SelectSize],
  isOpen: boolean,
  isFocused: boolean,
  isFocusVisible: boolean,
  isInvalid: boolean,
  isDisabled: boolean
): CSSProperties {
  const active = isOpen || isFocused || isFocusVisible;
  const borderColor = isInvalid
    ? 'var(--color-error)'
    : active
      ? 'var(--color-accent)'
      : 'var(--color-surface-border)';
  const ringColor = isInvalid ? 'var(--color-error-subtle)' : 'var(--color-accent-subtle)';

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    minHeight: config.height,
    padding: `0 ${config.paddingInline}px`,
    borderRadius: config.radius,
    background: 'var(--color-surface-solid)',
    border: `1px solid ${borderColor}`,
    boxShadow: active
      ? `0 0 0 3px ${ringColor}, inset 0 0.5px 0 var(--color-surface-highlight)`
      : 'var(--control-shadow-inset)',
    color: 'var(--color-text)',
    fontSize: config.fontSize,
    fontWeight: 'var(--weight-normal)',
    fontFamily: 'var(--font-sans)',
    letterSpacing: '-0.01em',
    outline: 'none',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition:
      'border-color var(--motion-fast) var(--motion-ease-out), box-shadow var(--motion-fast) var(--motion-ease-out), background var(--motion-fast) var(--motion-ease-out)',
  };
}

export function Select({
  label,
  description,
  errorMessage,
  size = 'md',
  options,
  className,
  style,
  triggerStyle,
  children,
  ...props
}: SelectProps) {
  const config = SIZE_CONFIG[size];
  const portalContainer = useInterfacePortalContainer();

  return (
    <AriaSelect
      {...props}
      className={cn('flex flex-col gap-1', className)}
      style={style}
      isInvalid={props.isInvalid ?? Boolean(errorMessage)}
    >
      {({ isOpen, isFocused, isFocusVisible, isInvalid, isDisabled }) => (
        <>
          {label && (
            <Label
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: config.labelSize,
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--color-text-secondary)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {label}
            </Label>
          )}

          <Button
            style={{
              ...getTriggerStyle(config, isOpen, isFocused, isFocusVisible, isInvalid, isDisabled),
              ...triggerStyle,
            }}
          >
            <SelectValue
              style={{
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            />
            <ChevronDown
              size={16}
              style={{
                flexShrink: 0,
                color: 'var(--color-text-muted)',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform var(--motion-fast) var(--motion-ease-out)',
              }}
            />
          </Button>

          {description && !isInvalid && (
            <Text
              slot="description"
              style={{
                marginTop: 5,
                fontSize: 13,
                color: 'var(--color-text-secondary)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {description}
            </Text>
          )}

          {errorMessage && (
            <Text
              slot="errorMessage"
              style={{
                marginTop: 5,
                fontSize: 13,
                color: 'var(--color-error-text)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {errorMessage}
            </Text>
          )}

          <Popover
            UNSTABLE_portalContainer={portalContainer ?? undefined}
            placement="bottom start"
            offset={8}
            className={cn(
              'w-[var(--trigger-width)] max-h-60 overflow-auto rounded-xl p-1 outline-none',
              'border border-[var(--color-surface-border)] bg-[var(--color-surface-solid)] shadow-[var(--shadow-md)]'
            )}
            style={{
              background: 'var(--color-surface-solid)',
              borderColor: 'var(--color-surface-border)',
              boxShadow: 'var(--shadow-md)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <ListBox className="outline-none" items={options}>
              {(option) => (
                <ListBoxItem
                  id={option.value as Key}
                  textValue={option.label}
                  isDisabled={option.disabled}
                  className={cn(
                    'cursor-pointer rounded-lg px-3 py-2.5 outline-none',
                    'text-[var(--text-sm)] text-[var(--color-text-secondary)]',
                    'hover:bg-[var(--color-surface-muted)] focus:bg-[var(--color-surface-muted)]',
                    'data-[selected]:bg-[var(--color-accent-subtle)] data-[selected]:text-[var(--color-accent-text)]',
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'
                  )}
                  style={{
                    lineHeight: 1.35,
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  {children ? children(option) : option.label}
                </ListBoxItem>
              )}
            </ListBox>
          </Popover>
        </>
      )}
    </AriaSelect>
  );
}
